import * as React from "react";
import * as moment from "moment";
import { useState } from "react";
import { CoughAnalysisSet as CoughAnalysisSetWP, QueryOfCoughAnalysisSet } from "webportalclient";
import { useWebPortalApiClient } from "../misc/api/WebPortalTokenClient";
import { Modal, ModalSize } from "../misc/components/modal";
import { LocalQueryTable } from "../misc/components/queryTable/LocalQueryTable";
import { QueryTableColumn } from "../misc/components/queryTable/QueryTable";
import { DateTimeAccuracy, LocalizedDateTime } from "../misc/components/LocalizedDateTime";
import { DatePickerNoForm } from "../forms/informedv2/DatePicker";
import LoadingWithMessage from "../misc/components/LoadingWithMessage";
import { useCurrentStudyConfigurationContext } from "../Core/CurrentStudyConfigurationContext";
import Alert, { AlertCategoryEnum } from "../misc/components/Alert";
import { CoughAnalysisSetTypeEnum } from "../misc/api/CoughAnalysisSetTypes";
import { OverreadStatusIdEnum } from "../misc/api/apiTypes";

export type WeeklyQAProps = {
    onClose: () => void;
}

export function WeeklyQAModal(props: WeeklyQAProps) {
    const [activeComponent, setActiveComponent] = useState(()=> <FilterUI onSubmit={onSubmitFilterUI} onCancel={props.onClose} />);
    const { study } = useCurrentStudyConfigurationContext();
    const client = useWebPortalApiClient().client;

    type FilterProps = {
        onSubmit: (startDate: Date, endDate: Date, onCancel: () => void) => void;
        onCancel: () => void;
    }

    type AnalystRange = {
        startIndex: number;
        endIndex: number;
    }

    type CoughAnalysisSet = CoughAnalysisSetWP & {
        isSelected?: boolean;
    }

    type AnalysisSetSelection = {
        analystRanges: AnalystRange[];
        analysisSets: CoughAnalysisSet[];
    }

    type AnalysisSetSelectionProps = {
        data: AnalysisSetSelection;
        onSubmit: (selection: CoughAnalysisSetWP[], onCancel: () => void) => void;
        onCancel: () => void;
    }

    type ProgressUIProps = {
        selectedItems: CoughAnalysisSetWP[];
        onClose: () => void;
    }

    function FilterUI({onSubmit, onCancel}: FilterProps) {
        const [startDate, setStartDate] = useState("");
        const [startDateError, setStartDateError] = useState<string | undefined>();
        const [endDate, setEndDate] = useState("");
        const [endDateError, setEndDateError] = useState<string | undefined>();
        const [submitting, setSubmitting] = useState(false);

        const Validate = () => {
            setSubmitting(false);

            if (startDate === "") {
                setStartDateError("Start date is required.");
            } else {
                setStartDateError(undefined);
            }

            if (endDate === "") {
                setEndDateError("End date is required.");
            } else {
                setEndDateError(undefined);
            }

            if (startDate === "" || endDate === "") {
                return;
            }

            var start = moment(startDate);
            var end = moment(endDate);
            var today = moment();

            if (end.isSameOrBefore(start) || end.isSameOrAfter(today)) {
                setEndDateError("End Date must be after Start Date and before Today's Date.");
                return;
            }

            setSubmitting(true);
            onSubmit(start.toDate(), end.toDate(), onCancel);
        }

        return (
            <>
                <div className="modal-body">
                    <div className="row">
                        <div className="col-md-12">
                            <h3 className="text-info">Weekly QA Filter</h3>
                        </div>
                        <div className="col-md-6">
                            <label htmlFor="startDate">Start Date</label>
                            <DatePickerNoForm id="startDate" onChange={(value) => setStartDate(value)} value={startDate} className='form-control' placeholder="DD-MM-YYYY" from={true} />
                            {startDateError === undefined? null:
                                <span className="field-validation-error">{startDateError}</span>
                            }
                        </div>

                        <div className="col-md-6">
                            <label htmlFor="endDate">End Date</label>
                            <DatePickerNoForm id="endDate" onChange={(value) => setEndDate(value)} value={endDate} className='form-control' placeholder="DD-MM-YYYY" from={false} />
                            {endDateError === undefined? null:
                                <span className="field-validation-error">{endDateError}</span>
                            }
                        </div>
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-primary" onClick={Validate} disabled={submitting}>Submit</button>
                    <button className="btn btn-primary btn-outline" onClick={() => onCancel()}>Cancel</button>
                </div>
            </>
        );
    }

    async function onSubmitFilterUI(startDate: Date, endDate: Date, onCancel: () => void) {
        const data = await LoadData(startDate, endDate);
        setActiveComponent(() => <SelectionUI data={data} onSubmit={onSubmitSelectionUI} onCancel={onCancel} />);
    }

    function LoadData(startDate: Date, endDate: Date): Promise<AnalysisSetSelection> {
        let analysisSetStatus = [
            OverreadStatusIdEnum.Pending,
            OverreadStatusIdEnum.Accepted
        ].map(e => `'${e}'`).join(',');

        let analysisSetTypes = [
            CoughAnalysisSetTypeEnum.Analysis,
            CoughAnalysisSetTypeEnum.ProjectQualityAssurance,
            CoughAnalysisSetTypeEnum.MonitoringQualityAssurance,
            CoughAnalysisSetTypeEnum.DifficultCougherQualityAssurance,
            CoughAnalysisSetTypeEnum.AuditQualityAssurance
        ].map(e => `'${e}'`).join(',');

        let filter = `(date(firstComplete) ge ${moment(startDate).format('YYYY-MM-DD')}) and (date(firstComplete) le ${moment(endDate).format('YYYY-MM-DD')}) and `;
        filter += `(status in (${analysisSetStatus})) and `;
        filter += `(type in (${analysisSetTypes})) and `;
        filter += `(session/visit/subject/site/studyId eq ${study.id})`

        let select = `id, session, analyst, statusName, typeName, firstComplete`;
        let orderBy = 'analyst';

        const updateResult = (data: QueryOfCoughAnalysisSet): AnalysisSetSelection => {
            const analysisSets: CoughAnalysisSet[] = data.value;
            const result: AnalysisSetSelection = {
                analystRanges: [],
                analysisSets: []
            };

            if (analysisSets == undefined || analysisSets.length == 0) {
                return result;
            }

            let start = 0, end = 0;

            // update incoming analysis sets
            for (let idx = 0; idx < analysisSets.length; ++idx) {
                // extend the type to include isSelected
                analysisSets[idx] = { ...analysisSets[idx], isSelected: false };

                if (analysisSets[start].analyst === analysisSets[idx].analyst) {
                    if (idx != analysisSets.length - 1) {
                        continue;
                    }

                    // this is for the decrement operation in next outer assignment statement
                    // to work correctly because we are at the last element of the array
                    ++idx;
                }

                end = --idx;

                // [1] save analyst index range
                result.analystRanges.push({ startIndex: start, endIndex: end });

                // [2] start randomizing records for this analyst
                for (let current = end; current >= start; --current) {
                    const newIdx = Math.floor(Math.random() * (current - start + 1)) + start;
                    [analysisSets[current], analysisSets[newIdx]] = [analysisSets[newIdx], analysisSets[current]];
                }

                start = end + 1;
            }

            // [3] update analyst isSelected in first record in range
            for (const analyst of result.analystRanges) {
                analysisSets[analyst.startIndex].isSelected = true;
            }

            result.analysisSets = analysisSets;

            return result;
        }

        return client.getCoughAnalysisSets(select, filter, orderBy, 'session($select=name)').then(updateResult);
    }

    function SelectionUI({data, onSubmit, onCancel}: AnalysisSetSelectionProps) {
        const [collection, setCollection] = useState(data.analysisSets);
        const [errorMessage, setErrorMessage] = useState("");
        const [validating, setValidating] = useState(false);

        const Checkbox = ({row}) => {
            const onCheckChanges = (e) => {
                setCollection(
                    collection.map(item => item.id == row.id? {...row, isSelected: e.currentTarget.checked }: item)
                );
            };

            return (
                <input type="checkbox" checked={row.isSelected} onChange={onCheckChanges} />
            );
        }

        const Validate = () => {
            setValidating(true);

            let invalidAnalyst: string[] = [];

            for(const range of data.analystRanges) {
                const found = collection
                                .slice(range.startIndex, range.endIndex + 1)
                                .some(item => item.isSelected);
                if (!found) {
                    invalidAnalyst.push(collection[range.startIndex].analyst);
                }
            }

            if (invalidAnalyst.length > 0) {
                setErrorMessage(
                    `the following analyst(s) need to have at least one analysis set selected: ${invalidAnalyst.join(', ')}.`
                );

                setValidating(false);
                return;
            }

            onSubmit(collection.filter(item => item.isSelected), onCancel);
        }

        return (
            <>
                <div className="modal-body">
                    <Alert show={errorMessage !== ""} category={AlertCategoryEnum.Danger}>{errorMessage}</Alert>
                    <div className="row">
                        <div className="col-md-12">
                            <h3 className="text-info">Cough Analysis Sets</h3>
                        </div>
                        <div className="col-md-12">
                            <LocalQueryTable collection={collection} defaultPageSize={10} rowUniqueIdentifier={row => row.id} query={{Data: collection}}>
                                <QueryTableColumn member="" header="" map={row => <Checkbox row={row} />} />
                                <QueryTableColumn member="" header="Session" map={row => row.session.name} />
                                <QueryTableColumn member="" header="Analyst" map={row => row.analyst} />
                                <QueryTableColumn member="" header="Type" map={row => row.typeName} />
                                <QueryTableColumn member="" header="Status" map={row => row.statusName} />
                                <QueryTableColumn member="" header="First Completed" map={row => <LocalizedDateTime time={row.firstComplete} accuracy={DateTimeAccuracy.Second} />} />
                            </LocalQueryTable>
                        </div>
                    </div>
                </div>
                <div className="modal-footer">
                    {collection.length == 0? null:
                      <button className="btn btn-primary" disabled={validating} onClick={Validate}>Accept</button>
                    }
                    <button className="btn btn-primary btn-outline" onClick={onCancel}>{collection.length == 0? "Close": "Cancel"}</button>
                </div>
            </>
        );
    }

    function onSubmitSelectionUI(selection: CoughAnalysisSetWP[], onClose: () => void) {
        setActiveComponent(() => <ProgressUI selectedItems={selection} onClose={onClose} />);
    }

    function ProgressUI({selectedItems, onClose}: ProgressUIProps) {
        let [count, setCount] = useState(0);

        React.useEffect(() => {
            const postData = async() => {
                for (const item of selectedItems) {
                    try {
                        setCount(++count);
                        await client.postWeeklyQA(item.id);
                    } catch {
                    }
                }

                onClose();
            };

            postData();
        }, [selectedItems]);

        return (
            <div className="modal-body">
                <div className="row">
                    <LoadingWithMessage message={`Updating ${count} of ${selectedItems.length}`} />
                </div>
            </div>
        );
    }

    return (
        <Modal header="Create Weekly QA Batch" show={true} size={ModalSize.Large}>
            {activeComponent}
        </Modal>
    );
}
